import type * as Sentry from '@sentry/react';

/**
 * Categories for third-party services, useful for filtering in Sentry.
 * Use `third_party.service` to filter by service name (e.g. "pendo")
 * and `third_party.category` to filter by category (e.g. "analytics").
 */
type ThirdPartyCategory =
  | 'analytics'
  | 'support'
  | 'payments'
  | 'advertising'
  | 'accessibility'
  | 'authentication'
  | 'media'
  | 'other';

/** Infer the span type from Sentry's beforeSendSpan option */
type SpanJSON = NonNullable<Sentry.BrowserOptions['beforeSendSpan']> extends (span: infer S) => any ? S : never;

interface ThirdPartyRule {
  /** Human-readable service name, used as the `third_party.service` tag */
  service: string;
  /** Broad category, used as the `third_party.category` tag */
  category: ThirdPartyCategory;
  /** URL substrings to match against span descriptions and breadcrumb URLs */
  urlPatterns: string[];
}

/**
 * Registry of third-party services and the URL patterns that identify their network requests.
 * Add new services here as needed — they'll automatically be tagged in Sentry spans, breadcrumbs, and events.
 */
const THIRD_PARTY_RULES: ThirdPartyRule[] = [
  {
    service: 'pendo',
    category: 'analytics',
    urlPatterns: ['pendo.io', 'pendo-static', 'cdn.pendo.io'],
  },
  {
    service: 'ada',
    category: 'support',
    urlPatterns: ['ada.support', 'static.ada.support'],
  },
  {
    service: 'zendesk',
    category: 'support',
    urlPatterns: ['zdassets.com', 'zendesk.com'],
  },
  {
    service: 'stripe',
    category: 'payments',
    urlPatterns: ['js.stripe.com', 'api.stripe.com', 'r.stripe.com', 'pm-hooks.stripe.com', 'm.stripe.com'],
  },
  {
    service: 'firebase',
    category: 'other',
    urlPatterns: ['firebaseinstallations.googleapis.com', 'fcmregistrations.googleapis.com', 'firebaseapp.com'],
  },
  {
    service: 'google-tag-manager',
    category: 'advertising',
    urlPatterns: ['googletagmanager.com', 'google-analytics.com', 'googleadservices.com'],
  },
  {
    service: 'facebook-pixel',
    category: 'advertising',
    urlPatterns: ['connect.facebook.net', 'facebook.com/tr'],
  },
  {
    service: 'recaptcha',
    category: 'authentication',
    urlPatterns: ['google.com/recaptcha', 'recaptcha.net'],
  },
  {
    service: 'accessibe',
    category: 'accessibility',
    urlPatterns: ['acsbapp.com'],
  },
  {
    service: 'giphy',
    category: 'media',
    urlPatterns: ['api.giphy.com', 'giphy.com'],
  },
  {
    service: 'adobe-pdf',
    category: 'media',
    urlPatterns: ['acrobatservices.adobe.com'],
  },
  {
    service: 'justifi',
    category: 'payments',
    urlPatterns: ['justifi.ai', 'justifi.tech'],
  },
  {
    service: 'okta',
    category: 'authentication',
    urlPatterns: ['okta.com'],
  },
  {
    service: 'sentry',
    category: 'other',
    urlPatterns: ['ingest.sentry.io', 'ingest.us.sentry.io'],
  },
];

/**
 * Match a URL string against the third-party rules.
 * Returns the matching rule, or undefined if no match.
 */
function matchThirdParty(url: string | undefined): ThirdPartyRule | undefined {
  if (!url) return undefined;
  const lower = url.toLowerCase();
  return THIRD_PARTY_RULES.find((rule) => rule.urlPatterns.some((pattern) => lower.includes(pattern)));
}

/**
 * A `beforeSendSpan` handler that tags third-party network spans with
 * `third_party.service` and `third_party.category` attributes.
 *
 * These attributes can be used in Sentry to filter or search for
 * third-party traffic (e.g. `third_party.service:pendo`).
 *
 * Compose with any existing `beforeSendSpan` handler:
 * ```ts
 * beforeSendSpan(span) {
 *   const tagged = tagThirdPartySpan(span);
 *   // your other span filtering logic
 *   return tagged;
 * }
 * ```
 */
export function tagThirdPartySpan(span: SpanJSON): SpanJSON {
  const description = span.description ?? '';
  const match = matchThirdParty(description);

  if (match) {
    return {
      ...span,
      data: {
        ...span.data,
        'third_party.service': match.service,
        'third_party.category': match.category,
        'third_party.is_third_party': true,
      },
    };
  }

  return span;
}

/**
 * A `beforeBreadcrumb` handler that tags third-party HTTP breadcrumbs with
 * `third_party.service` and `third_party.category` in the breadcrumb data.
 *
 * Use in Sentry init:
 * ```ts
 * beforeBreadcrumb(breadcrumb, hint) {
 *   return tagThirdPartyBreadcrumb(breadcrumb);
 * }
 * ```
 */
export function tagThirdPartyBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  if (breadcrumb.category !== 'xhr' && breadcrumb.category !== 'fetch') {
    return breadcrumb;
  }

  const url = breadcrumb.data?.url as string | undefined;
  const match = matchThirdParty(url);

  if (match) {
    return {
      ...breadcrumb,
      data: {
        ...breadcrumb.data,
        'third_party.service': match.service,
        'third_party.category': match.category,
        'third_party.is_third_party': true,
      },
    };
  }

  return breadcrumb;
}

/**
 * A `beforeSend` handler that tags error events with third-party info
 * when the error originated from a third-party script (detected via stack frames).
 *
 * Use in Sentry init (compose with existing beforeSend):
 * ```ts
 * beforeSend(event, hint) {
 *   const tagged = tagThirdPartyEvent(event);
 *   // your existing logic
 *   return tagged;
 * }
 * ```
 */
export function tagThirdPartyEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  const frames = event.exception?.values?.flatMap((exc) => exc.stacktrace?.frames ?? []) ?? [];
  const urls = frames.map((frame) => frame.filename).filter(Boolean) as string[];

  // Also check the request URL
  if (event.request?.url) {
    urls.push(event.request.url);
  }

  for (const url of urls) {
    const match = matchThirdParty(url);
    if (match) {
      return {
        ...event,
        tags: {
          ...event.tags,
          'third_party.service': match.service,
          'third_party.category': match.category,
          'third_party.is_third_party': 'true',
        },
      };
    }
  }

  return event;
}

export { THIRD_PARTY_RULES, type ThirdPartyRule, type ThirdPartyCategory };
